import { getAppState, subscribe } from '../app/index';
import { onLocaleChange, t } from '../i18n';
import { defaultTopicFromPath, pageHelpContext } from './defaultTopic';
import {
  canAccessHelpTopic,
  filterHelpArticles,
  guestAllowedArticleIds,
  guestHelpTopicForPath,
  parentHelpArticleId,
  type HelpAccessContext,
} from './helpAccess';
import enContent from './content/en.html?raw';
import nlContent from './content/nl.html?raw';
import frContent from './content/fr.html?raw';
import deContent from './content/de.html?raw';

const LAST_KEY = 'shareweb:help-topic';

const CONTENT_BY_LOCALE: Record<string, string> = {
  en: enContent,
  nl: nlContent,
  fr: frContent,
  de: deContent,
};

type ShowOpts = {
  hash?: boolean;
  skipHighlight?: boolean;
  skipMatchNav?: boolean;
};

let overlay: HTMLDivElement | null = null;
let navEl: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let searchEl: HTMLInputElement | null = null;
let articles: HTMLElement[] = [];
let currentId = '';
let standalone = false;
let matchIndex = 0;
let matchQuery = '';
let contentLocale = '';
let bodyClickBound = false;
let booted = false;
let localeUnsub: (() => void) | null = null;
let sessionUnsub: (() => void) | null = null;

function contentForLocale(locale: string): string {
  return CONTENT_BY_LOCALE[locale] ?? CONTENT_BY_LOCALE.en;
}

function currentLocale(): string {
  return localStorage.getItem('shareweb:locale')
    ?? document.documentElement.lang
    ?? 'en';
}

function remember(id: string): void {
  currentId = id || currentId;
  if (!currentId) return;
  try {
    sessionStorage.setItem(LAST_KEY, currentId);
  } catch {
    /* ignore */
  }
}

function lastTopic(): string | null {
  try {
    return sessionStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

function viewerAccessContext(path = window.location.pathname): HelpAccessContext {
  const state = getAppState();
  const roles = state.kind === 'authenticated' ? state.session.roles : [];
  return { path, roles };
}

function allowedArticles(path = window.location.pathname): HTMLElement[] {
  return filterHelpArticles(articles, viewerAccessContext(path));
}

function helpShellEl(): HTMLElement | null {
  if (bodyEl) {
    const shell = bodyEl.closest('.help-shell');
    if (shell instanceof HTMLElement) return shell;
  }
  return overlay?.querySelector('.help-shell') ?? null;
}

function applySingleTopicChrome(): void {
  const shell = helpShellEl();
  if (!shell) return;
  shell.classList.toggle('help-shell--single', allowedArticles().length <= 1);
}

function fallbackTopicId(path = window.location.pathname): string {
  const guestTopic = guestHelpTopicForPath(path);
  if (guestTopic) return guestTopic;
  const allowed = allowedArticles(path);
  if (allowed.some((a) => a.id === 'getting-started')) return 'getting-started';
  return allowed[0]?.id || 'getting-started';
}

export function resolveDefaultTopic(path = window.location.pathname): string {
  const ctx = viewerAccessContext(path);
  if (standalone) {
    const hash = (location.hash || '').replace(/^#/, '');
    if (hash && canAccessHelpTopic(hash, ctx)) return hash;
  }
  // Guest routes: prefer page context (active SEDR tab) so F1 matches the screen.
  if (guestAllowedArticleIds(path)) {
    const fromPath = defaultTopicFromPath(path, pageHelpContext());
    if (canAccessHelpTopic(fromPath, ctx)) return fromPath;
    return guestHelpTopicForPath(path) || fallbackTopicId(path);
  }
  const last = lastTopic();
  if (last && canAccessHelpTopic(last, ctx)) return last;
  const fromPath = defaultTopicFromPath(path, pageHelpContext());
  if (canAccessHelpTopic(fromPath, ctx)) return fromPath;
  return fallbackTopicId(path);
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return el.isContentEditable;
}

function modalOpen(): boolean {
  return Boolean(document.querySelector('.modal-backdrop'));
}

function applyChromeLabels(root: ParentNode): void {
  const title = root.querySelector('#helpTitle');
  if (title) title.textContent = t('help.title');

  if (searchEl) {
    searchEl.placeholder = t('help.search');
    searchEl.setAttribute('aria-label', t('help.search'));
  }

  const closeBtn = root.querySelector('#helpClose');
  if (closeBtn) {
    closeBtn.textContent = t('help.close');
    closeBtn.setAttribute('title', `${t('help.close')} (Esc)`);
  }

  const matchNav = root.querySelector('.help-match-nav');
  if (matchNav) {
    const prev = matchNav.querySelector('[data-help-match="prev"]');
    const next = matchNav.querySelector('[data-help-match="next"]');
    prev?.setAttribute('aria-label', t('help.previousMatch'));
    prev?.setAttribute('title', t('help.previousMatch'));
    next?.setAttribute('aria-label', t('help.nextMatch'));
    next?.setAttribute('title', t('help.nextMatch'));
  }

  const topicsNav = root.querySelector('#helpNav');
  topicsNav?.setAttribute('aria-label', t('help.topics'));
}

function ensureOverlay(): void {
  if (standalone || overlay) return;
  overlay = document.createElement('div');
  overlay.id = 'helpOverlay';
  overlay.className = 'help-overlay';
  overlay.hidden = true;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'helpTitle');
  overlay.innerHTML = `
    <div class="help-shell">
      <div class="help-toolbar">
        <h1 id="helpTitle"></h1>
        <div class="help-search-wrap">
          <input id="helpSearch" class="help-search" type="search" />
          <div class="help-match-nav" hidden>
            <button type="button" data-help-match="prev">&lt;</button>
            <span class="help-match-count" aria-live="polite">0/0</span>
            <button type="button" data-help-match="next">&gt;</button>
          </div>
        </div>
        <button type="button" class="button ghost" id="helpClose"></button>
      </div>
      <div class="help-layout">
        <nav id="helpNav" class="help-nav"></nav>
        <div id="helpBody" class="help-body"><p class="help-muted"></p></div>
      </div>
    </div>
  `;
  document.body.append(overlay);
  navEl = overlay.querySelector('#helpNav');
  bodyEl = overlay.querySelector('#helpBody');
  searchEl = overlay.querySelector('#helpSearch');
  const loading = bodyEl?.querySelector('.help-muted');
  if (loading) loading.textContent = t('help.loading');
  applyChromeLabels(overlay);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) closeHelp();
  });
  overlay.querySelector('#helpClose')?.addEventListener('click', () => closeHelp());
  bindSearchControls(overlay);
}

function bindSearchControls(root: ParentNode): void {
  if (searchEl && !(searchEl as HTMLInputElement & { _helpBound?: boolean })._helpBound) {
    (searchEl as HTMLInputElement & { _helpBound?: boolean })._helpBound = true;
    searchEl.addEventListener('input', () => filterNav(searchEl!.value));
    searchEl.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      stepMatch(event.shiftKey ? -1 : 1);
    });
  }
  const matchNav = root.querySelector('.help-match-nav');
  if (!matchNav || (matchNav as HTMLElement & { _helpBound?: boolean })._helpBound) return;
  (matchNav as HTMLElement & { _helpBound?: boolean })._helpBound = true;
  matchNav.querySelector('[data-help-match="prev"]')?.addEventListener('click', () => stepMatch(-1));
  matchNav.querySelector('[data-help-match="next"]')?.addEventListener('click', () => stepMatch(1));
}

function loadContent(force = false): Promise<void> {
  const locale = currentLocale().slice(0, 2);
  if (!force && contentLocale === locale && articles.length && bodyEl) {
    buildNav(searchEl?.value ?? '');
    applySingleTopicChrome();
    return Promise.resolve();
  }

  if (!bodyEl) return Promise.resolve();

  bodyEl.innerHTML = contentForLocale(locale);
  contentLocale = locale;
  articles = Array.from(bodyEl.querySelectorAll('article.help-article'));
  for (const article of articles) article.hidden = true;
  ensurePageChrome();
  if (!bodyClickBound) {
    bodyEl.addEventListener('click', onHelpBodyClick);
    bodyClickBound = true;
  }
  buildNav(searchEl?.value ?? '');
  applySingleTopicChrome();
  return Promise.resolve();
}

function ensurePageChrome(): void {
  if (!bodyEl) return;
  if (!bodyEl.querySelector('.help-pager')) {
    const pager = document.createElement('nav');
    pager.className = 'help-pager';
    pager.setAttribute('aria-label', t('help.pages'));
    pager.innerHTML = `
      <button type="button" class="button ghost" data-help-page="prev"></button>
      <span class="help-pager-label"></span>
      <button type="button" class="button ghost" data-help-page="next"></button>
    `;
    bodyEl.append(pager);
    pager.querySelector('[data-help-page="prev"]')?.addEventListener('click', () => stepPage(-1));
    pager.querySelector('[data-help-page="next"]')?.addEventListener('click', () => stepPage(1));
  }
}

function stepPage(dir: number): void {
  const allowed = allowedArticles();
  const idx = allowed.findIndex((a) => a.id === currentId);
  const next = allowed[idx + dir];
  if (next) showTopic(next.id, { hash: standalone });
}

function esc(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cssEscape(id: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(id);
  return String(id).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function escapeRegExp(s: string): string {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function articleSearchText(art: HTMLElement): string {
  return [
    art.id,
    art.getAttribute('data-nav') || '',
    art.getAttribute('data-search') || '',
    art.getAttribute('data-group') || '',
    art.textContent || '',
  ].join(' ').toLowerCase();
}

function highlightTextHtml(raw: string, query: string): string {
  const text = String(raw || '');
  const q = String(query || '').trim();
  if (!q) return esc(text);
  const re = new RegExp(escapeRegExp(q), 'gi');
  let out = '';
  let last = 0;
  let match: RegExpExecArray | null;
  re.lastIndex = 0;
  while ((match = re.exec(text))) {
    out += esc(text.slice(last, match.index));
    out += `<mark class="help-mark">${esc(match[0])}</mark>`;
    last = match.index + match[0].length;
    if (!match[0].length) break;
  }
  return out + esc(text.slice(last));
}

function buildNav(query?: string): void {
  if (!navEl) return;
  const q = String(query || '').trim().toLowerCase();
  const visible = allowedArticles().filter((art) => {
    if (!q) return true;
    return articleSearchText(art).includes(q);
  });
  const groups: string[] = [];
  const byGroup = new Map<string, HTMLElement[]>();
  for (const art of visible) {
    const group = art.getAttribute('data-group') || t('help.title');
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
      groups.push(group);
    }
    byGroup.get(group)!.push(art);
  }

  let html = '';
  for (const group of groups) {
    html += `<p class="help-nav-group">${esc(group)}</p>`;
    for (const art of byGroup.get(group)!) {
      html += `<button type="button" class="help-nav-item" data-help-nav="${esc(art.id)}">${
        highlightTextHtml(art.getAttribute('data-nav') || art.id, q)
      }</button>`;
    }
  }
  html += `<p class="help-nav-empty"${visible.length ? ' hidden' : ''}>${esc(t('help.noMatches'))}</p>`;
  navEl.innerHTML = html;
  for (const btn of navEl.querySelectorAll('[data-help-nav]')) {
    btn.addEventListener('click', () => {
      showTopic(btn.getAttribute('data-help-nav') || '', { hash: standalone });
    });
  }
  highlightNav(currentId);
  applySearchHighlight();
  applySingleTopicChrome();
}

function filterNav(q: string): void {
  const query = String(q || '').trim();
  matchQuery = '';
  buildNav(query);
  if (query) {
    const matches = allowedArticles().filter((a) => articleSearchText(a).includes(query.toLowerCase()));
    if (matches.length && matches.every((a) => a.id !== currentId)) {
      showTopic(matches[0]!.id, { hash: standalone, skipHighlight: true, skipMatchNav: true });
    }
  } else if (currentId) {
    highlightNav(currentId);
  }
  revealMatch(true);
}

function highlightNav(artId: string): void {
  if (!navEl) return;
  for (const btn of navEl.querySelectorAll('[data-help-nav]')) {
    const on = btn.getAttribute('data-help-nav') === artId;
    btn.classList.toggle('active', on);
    if (!on) continue;
    const navRect = navEl.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.top < navRect.top || btnRect.bottom > navRect.bottom) {
      navEl.scrollTop += btnRect.top - navRect.top - 8;
    }
  }
}

function findAlias(id: string): HTMLElement | null {
  if (!bodyEl) return null;
  return bodyEl.querySelector(`[data-alias~="${cssEscape(id)}"]`);
}

function showTopic(id: string, opts?: ShowOpts): void {
  const replaceHash = standalone || Boolean(opts?.hash);
  const ctx = viewerAccessContext();
  let topicId = id || resolveDefaultTopic();
  let target = bodyEl
    ? (bodyEl.querySelector(`#${cssEscape(topicId)}`) as HTMLElement | null) || findAlias(topicId)
    : null;
  let art = target
    ? ((target.closest?.('article.help-article') as HTMLElement | null) || target)
    : null;

  if (!art || !canAccessHelpTopic(art.id || topicId, ctx)) {
    const fallbackId = fallbackTopicId();
    target = bodyEl
      ? (bodyEl.querySelector(`#${cssEscape(fallbackId)}`) as HTMLElement | null)
      : null;
    art = target;
    topicId = fallbackId;
  }
  if (!art && allowedArticles().length) {
    art = allowedArticles()[0]!;
    target = art;
  }
  if (!art || !target) return;

  const artId = art.id || parentHelpArticleId(topicId);
  remember(artId);
  for (const a of articles) a.hidden = a !== art;
  highlightNav(artId);
  updatePager(art);
  applySingleTopicChrome();

  if (bodyEl) bodyEl.scrollTop = 0;
  if (target !== art && bodyEl) {
    requestAnimationFrame(() => {
      if (!bodyEl) return;
      const sRect = bodyEl.getBoundingClientRect();
      const tRect = target!.getBoundingClientRect();
      bodyEl.scrollTop = Math.max(0, tRect.top - sRect.top - 8);
    });
  }

  if (replaceHash) {
    const next = `#${target.id || artId}`;
    if (location.hash !== next) {
      history.replaceState(null, '', `${location.pathname}${location.search}${next}`);
    }
  }

  if (!opts?.skipHighlight) applySearchHighlight();
  if (opts?.skipMatchNav) updateMatchNav();
  else revealMatch(true);
}

function updatePager(art: HTMLElement): void {
  const pager = bodyEl?.querySelector('.help-pager');
  if (!pager) return;
  const allowed = allowedArticles();
  const idx = allowed.findIndex((a) => a === art);
  const prev = idx > 0 ? allowed[idx - 1] : null;
  const next = idx >= 0 && idx < allowed.length - 1 ? allowed[idx + 1] : null;
  const prevBtn = pager.querySelector('[data-help-page="prev"]') as HTMLButtonElement;
  const nextBtn = pager.querySelector('[data-help-page="next"]') as HTMLButtonElement;
  const label = pager.querySelector('.help-pager-label');
  prevBtn.disabled = !prev;
  nextBtn.disabled = !next;
  prevBtn.textContent = prev
    ? `← ${prev.getAttribute('data-nav') || prev.id}`
    : t('help.previous');
  nextBtn.textContent = next
    ? `${next.getAttribute('data-nav') || next.id} →`
    : t('help.next');
  if (label) label.textContent = `${Math.max(idx, 0) + 1} / ${allowed.length}`;
}

function clearMarks(root: HTMLElement | null): void {
  if (!root) return;
  for (const mark of root.querySelectorAll('mark.help-mark')) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  }
}

function highlightIn(root: HTMLElement, query: string): void {
  clearMarks(root);
  const q = String(query || '').trim();
  if (!q) return;
  const re = new RegExp(escapeRegExp(q), 'gi');
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !String(node.nodeValue).trim()) return NodeFilter.FILTER_REJECT;
      let el = node.parentElement;
      while (el && el !== root) {
        const tag = el.tagName;
        if (tag === 'SVG' || tag === 'MARK' || el.closest?.('svg')) {
          return NodeFilter.FILTER_REJECT;
        }
        el = el.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  for (const textNode of nodes) {
    const text = textNode.nodeValue ?? '';
    re.lastIndex = 0;
    if (!re.test(text)) continue;
    re.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text))) {
      if (match.index > last) {
        frag.appendChild(document.createTextNode(text.slice(last, match.index)));
      }
      const mark = document.createElement('mark');
      mark.className = 'help-mark';
      mark.textContent = match[0];
      frag.appendChild(mark);
      last = match.index + match[0].length;
      if (!match[0].length) break;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}

function applySearchHighlight(query?: string): void {
  const q = query != null ? query : (searchEl ? String(searchEl.value || '').trim() : '');
  for (const art of allowedArticles()) highlightIn(art, q);
}

function collectMarks(): Array<{ art: HTMLElement; mark: HTMLElement }> {
  const list: Array<{ art: HTMLElement; mark: HTMLElement }> = [];
  for (const art of allowedArticles()) {
    for (const mark of art.querySelectorAll('mark.help-mark')) {
      list.push({ art, mark: mark as HTMLElement });
    }
  }
  return list;
}

function matchNavEl(): HTMLElement | null {
  const root = overlay || document;
  return root.querySelector('.help-match-nav');
}

function updateMatchNav(): void {
  const nav = matchNavEl();
  if (!nav) return;
  const q = searchEl ? String(searchEl.value || '').trim() : '';
  const marks = collectMarks();
  if (!q || !marks.length) {
    nav.hidden = true;
    return;
  }
  nav.hidden = false;
  if (matchIndex < 0 || matchIndex >= marks.length) matchIndex = 0;
  const count = nav.querySelector('.help-match-count');
  if (count) count.textContent = `${matchIndex + 1}/${marks.length}`;
  marks.forEach((item, i) => item.mark.classList.toggle('help-mark-current', i === matchIndex));
}

function scrollMarkIntoView(mark: HTMLElement): void {
  if (!bodyEl) return;
  const sRect = bodyEl.getBoundingClientRect();
  const tRect = mark.getBoundingClientRect();
  const pad = 56;
  if (tRect.top >= sRect.top + pad && tRect.bottom <= sRect.bottom - pad) return;
  bodyEl.scrollTop += tRect.top - sRect.top - Math.min(96, sRect.height / 3);
}

function goToMatch(index: number, opts?: { alreadyOnTopic?: boolean }): void {
  const marks = collectMarks();
  if (!marks.length) {
    updateMatchNav();
    return;
  }
  matchIndex = ((index % marks.length) + marks.length) % marks.length;
  const item = marks[matchIndex]!;
  const alreadyOnTopic = Boolean(opts?.alreadyOnTopic) && item.art.id === currentId;
  if (!alreadyOnTopic && item.art.id !== currentId) {
    showTopic(item.art.id, { hash: standalone, skipHighlight: true, skipMatchNav: true });
  }
  updateMatchNav();
  scrollMarkIntoView(item.mark);
}

function revealMatch(preferCurrentPage: boolean): void {
  const q = searchEl ? String(searchEl.value || '').trim() : '';
  const marks = collectMarks();
  if (!q || !marks.length) {
    matchIndex = 0;
    matchQuery = q;
    updateMatchNav();
    return;
  }
  if (preferCurrentPage || matchQuery !== q) {
    matchQuery = q;
    const onPage = marks.findIndex((item) => item.art.id === currentId);
    if (onPage >= 0) matchIndex = onPage;
    else if (!preferCurrentPage) matchIndex = 0;
  }
  const item = marks[Math.min(matchIndex, marks.length - 1)];
  if (preferCurrentPage && item && item.art.id !== currentId) {
    updateMatchNav();
    return;
  }
  goToMatch(matchIndex, { alreadyOnTopic: true });
}

function stepMatch(dir: number): void {
  if (!collectMarks().length) return;
  goToMatch(matchIndex + dir);
}

function onHelpBodyClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element) || !bodyEl) return;
  const anchor = target.closest('a[href^="#"]');
  if (!(anchor instanceof HTMLAnchorElement) || !bodyEl.contains(anchor)) return;
  const id = (anchor.getAttribute('href') || '').replace(/^#/, '');
  if (!id) return;
  event.preventDefault();
  showTopic(id, { hash: standalone });
}

export function isHelpOpen(): boolean {
  return standalone || Boolean(overlay && !overlay.hidden);
}

export async function openHelp(
  topicId?: string | null,
  opts?: { focusSearch?: boolean },
): Promise<void> {
  const focusSearch = Boolean(opts?.focusSearch);
  if (standalone) {
    await loadContent();
    showTopic(topicId || resolveDefaultTopic(), { hash: true });
    if (focusSearch && searchEl) searchEl.focus();
    return;
  }
  ensureOverlay();
  if (overlay) overlay.hidden = false;
  document.body.classList.add('help-open');
  await loadContent();
  showTopic(topicId || resolveDefaultTopic());
  if (focusSearch && searchEl) searchEl.focus();
}

export function closeHelp(): void {
  if (standalone || !overlay) return;
  overlay.hidden = true;
  document.body.classList.remove('help-open');
}

export function leaveHelpPageMode(): void {
  if (!standalone) return;
  standalone = false;
  navEl = null;
  bodyEl = null;
  searchEl = null;
  articles = [];
  contentLocale = '';
  bodyClickBound = false;
}

export async function enterHelpPageMode(root: HTMLElement): Promise<void> {
  closeHelp();
  standalone = true;
  navEl = root.querySelector('#helpNav');
  bodyEl = root.querySelector('#helpBody');
  searchEl = root.querySelector('#helpSearch');
  applyChromeLabels(root);
  bindSearchControls(root);
  await loadContent(true);
  showTopic(resolveDefaultTopic(), { hash: true });
}

async function reloadForLocale(): Promise<void> {
  if (!bodyEl) return;
  const topic = currentId || resolveDefaultTopic();
  const root = overlay && !overlay.hidden ? overlay : (standalone ? document : null);
  if (root instanceof HTMLElement || root === document) {
    applyChromeLabels(root === document ? document : root);
  }
  await loadContent(true);
  if (isHelpOpen() || standalone) {
    showTopic(topic, { hash: standalone, skipHighlight: false });
  }
}

function onKey(event: KeyboardEvent): void {
  if (event.key === 'F1') {
    event.preventDefault();
    if (standalone) {
      if (searchEl) searchEl.focus();
      else void openHelp(null, { focusSearch: true });
    } else if (isHelpOpen()) {
      searchEl?.focus();
    } else {
      void openHelp(null, { focusSearch: true });
    }
    return;
  }

  if (!isHelpOpen() || standalone) return;

  if (event.key === 'Escape') {
    if (modalOpen()) return;
    event.preventDefault();
    event.stopPropagation();
    closeHelp();
    return;
  }

  if ((event.key === 'Delete' || event.key === 'Backspace') && !isTypingTarget(event.target)) {
    event.stopPropagation();
  }
}

function onDocumentClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const tip = target.closest('[data-help-topic]');
  if (tip) {
    event.preventDefault();
    event.stopPropagation();
    void openHelp(tip.getAttribute('data-help-topic'));
    return;
  }

  const openBtn = target.closest('[data-help-open]');
  if (openBtn) {
    event.preventDefault();
    void openHelp(null, { focusSearch: true });
  }
}

function refreshAccess(): void {
  if (!bodyEl || !articles.length) return;
  buildNav(searchEl?.value ?? '');
  applySingleTopicChrome();
  if (!(isHelpOpen() || standalone)) return;
  const ctx = viewerAccessContext();
  if (!currentId || !canAccessHelpTopic(currentId, ctx)) {
    showTopic(fallbackTopicId(), { hash: standalone });
    return;
  }
  const art = allowedArticles().find((a) => a.id === currentId);
  if (art) updatePager(art);
}

export function bootHelp(): void {
  if (booted) return;
  booted = true;
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKey, true);
  localeUnsub = onLocaleChange(() => {
    void reloadForLocale();
  });
  sessionUnsub = subscribe(() => {
    refreshAccess();
  });
  window.addEventListener('hashchange', () => {
    if (!standalone) return;
    const id = (location.hash || '').replace(/^#/, '');
    if (id) showTopic(id);
  });

  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
  if (typeof idle === 'function') {
    idle(() => {
      ensureOverlay();
      void loadContent();
    });
  } else {
    setTimeout(() => {
      ensureOverlay();
      void loadContent();
    }, 1800);
  }
}

/** Test helpers */
export const __helpTest = {
  articleSearchText,
  findArticles: () => articles,
  loadContent,
  showTopic,
  setBodyForTest(el: HTMLElement): void {
    bodyEl = el;
    articles = Array.from(el.querySelectorAll('article.help-article'));
    contentLocale = 'en';
  },
  reset(): void {
    leaveHelpPageMode();
    closeHelp();
    if (overlay) {
      overlay.remove();
      overlay = null;
    }
    navEl = null;
    bodyEl = null;
    searchEl = null;
    articles = [];
    currentId = '';
    contentLocale = '';
    bodyClickBound = false;
    matchIndex = 0;
    matchQuery = '';
  },
  refreshAccess,
};

// silence unused in production builds
void localeUnsub;
void sessionUnsub;
